/**
 * studioApi.ts
 *
 * Typed API client functions for the Studio Desk (platform-builder home) and
 * its supporting recent-templates list. `GET /studio/desk` and the
 * validate-only publish route live on schema-service; recent-templates
 * touch/list live on search-service (mirrors `recent-spheres`).
 */

import type { ApiClient } from '@center-technologies/shared-api-clients';
import type { StudioDesk, PublishFinding, StudioTemplateCard } from 'types/studio';
import type { ChannelItemType } from 'types/channel';
import type { ChannelItemLookupEntry } from './marketplaceApi';

/** Query params for GET /studio/desk. All optional — server falls back to dynamic config. */
export interface GetStudioDeskParams {
  recentLimit?: number;
  staleDays?: number;
  kinds?: string[];
}

/**
 * Fetch the caller's Studio Desk aggregate (recents, findings, per-type counts).
 *
 * @param schemaClient - Schema service API client
 * @param params - Optional bounds; omitted values use server-side dynamic config
 */
export async function getStudioDesk(
  schemaClient: ApiClient,
  params: GetStudioDeskParams = {}
): Promise<StudioDesk> {
  const queryParams = new URLSearchParams();
  if (params.recentLimit !== undefined) queryParams.set('recentLimit', String(params.recentLimit));
  if (params.staleDays !== undefined) queryParams.set('staleDays', String(params.staleDays));
  if (params.kinds?.length) queryParams.set('kinds', params.kinds.join(','));

  const query = queryParams.toString();
  const suffix = query ? `?${query}` : '';
  return schemaClient.get<StudioDesk>(`/studio/desk${suffix}`);
}

/**
 * Validate a template version's publish-readiness without publishing it.
 *
 * @param schemaClient - Schema service API client
 * @param systemName - Template system name
 * @param version - Version number
 */
export async function validateTemplateVersion(
  schemaClient: ApiClient,
  systemName: string,
  version: number
): Promise<{ findings: PublishFinding[] }> {
  return schemaClient.post<{ findings: PublishFinding[] }>(
    `/templates/${encodeURIComponent(systemName)}/versions/${version}/validate`
  );
}

/**
 * Record that the caller opened or saved a template, for the desk's Resume
 * list. Fire-and-forget: callers should not surface failures to the user
 * (mirrors `recent-spheres` touch semantics).
 *
 * @param searchClient - Search service API client
 * @param templateId - Template id
 */
export async function touchRecentTemplate(searchClient: ApiClient, templateId: string): Promise<void> {
  await searchClient.post<void>(`/recent-templates/${encodeURIComponent(templateId)}`);
}

/** Query params for GET /studio/templates. `types` is required — the caller (a browse builder) always knows its own `templateTypes`. */
export interface GetStudioTemplatesParams {
  types: string[];
  status?: 'draft' | 'published';
  q?: string;
}

/**
 * Fetch the caller's permission-honest gallery card list for a set of template types
 * (EAINT-873 SC1). Never widens what the server returns — `degraded: true` means the
 * caller should render an error/empty state, not fall back to a stale or fabricated list.
 *
 * @param schemaClient - Schema service API client
 * @param params - `types` (required) plus optional `status`/`q` filters
 */
/**
 * Template types whose cards can report a `status` ('draft' | 'published'). Aspects have real
 * `template_version` drafts; arc/orbit families derive lifecycle from their type table's
 * `is_published` (owner decision 2026-09-01). Approach/schema/metaspace report `status: null`
 * forever, so a gallery whose `templateTypes` has no overlap here hides its status filter.
 */
export const STATUS_REPORTING_TEMPLATE_TYPES: readonly string[] = ['aspect', 'arc_template', 'orbit_template'];

export async function getStudioTemplates(
  schemaClient: ApiClient,
  params: GetStudioTemplatesParams
): Promise<{ templates: StudioTemplateCard[]; degraded: boolean }> {
  const queryParams = new URLSearchParams();
  queryParams.set('types', params.types.join(','));
  if (params.status) queryParams.set('status', params.status);
  if (params.q) queryParams.set('q', params.q);
  return schemaClient.get<{ templates: StudioTemplateCard[]; degraded: boolean }>(
    `/studio/templates?${queryParams.toString()}`
  );
}

export interface LookupChannelItemsBatchItem {
  itemType: ChannelItemType;
  itemSystemName: string;
}

export interface LookupChannelItemsBatchResultRow {
  itemType: ChannelItemType;
  itemSystemName: string;
  channels: ChannelItemLookupEntry[];
}

/**
 * Batched sibling of `lookupChannelItems` (marketplaceApi.ts) — one round trip answers
 * "which channels publish this?" for a whole gallery of cards (EAINT-873). Same
 * per-item visibility rules as the single lookup; missing/unviewable pairs return an
 * empty `channels` array, indistinguishable from unlisted — never treat an empty array
 * as a lookup failure (that's a rejected promise instead).
 *
 * @param schemaClient - Schema service API client
 * @param items - (itemType, itemSystemName) pairs, capped server-side at 100
 */
export async function lookupChannelItemsBatch(
  schemaClient: ApiClient,
  items: LookupChannelItemsBatchItem[]
): Promise<LookupChannelItemsBatchResultRow[]> {
  const response = await schemaClient.post<{ results: LookupChannelItemsBatchResultRow[] }>(
    '/channels/items/lookup-batch',
    { items }
  );
  return response?.results ?? [];
}
